import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiPut } from '@/context/AuthContext';
import { useAuth } from '@/context/AuthContext';
import { API_BASE_URL } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, MessageCircle, Plus, Send, Smile, Users, Wifi, WifiOff } from 'lucide-react';

function chatSocketUrl() {
  const token = encodeURIComponent(localStorage.getItem('token') || '');
  const wsBase = API_BASE_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
  return `${wsBase}/ws/chat?token=${token}`;
}

function formatTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

function initials(name) {
  return (name || 'G')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

const inputClass = 'w-full rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-blue-500';
const emojis = ['😀', '😄', '😂', '🥳', '❤️', '👍', '👎', '🎉', '🍻', '🥂', '🍷', '🍺', '🔥', '👀', '🙌', '😎', '🤣', '😅', '😍', '🤔', '🎵', '💃', '🕺'];

export default function ChatPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [activeId, setActiveId] = useState('public');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupMemberDraft, setGroupMemberDraft] = useState([]);
  const [ownerTransferId, setOwnerTransferId] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const socketRef = useRef(null);
  const activeIdRef = useRef(activeId);
  const bottomRef = useRef(null);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeId) || conversations[0],
    [conversations, activeId],
  );
  const isGroupOwner = activeConversation?.type === 'group' && String(activeConversation.created_by) === String(user?.id);
  const transferableMembers = (activeConversation?.members || []).filter((member) => String(member.id) !== String(user?.id));

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const loadConversations = useCallback(async () => {
    const res = await apiGet('/api/chat/conversations');
    const nextConversations = res.data.conversations || [];
    setConversations(nextConversations);
    if (!nextConversations.some((conversation) => conversation.id === activeIdRef.current)) {
      setActiveId(nextConversations[0]?.id || 'public');
    }
    return nextConversations;
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    setMessagesLoading(true);
    try {
      const res = await apiGet(`/api/chat/messages?conversation_id=${encodeURIComponent(conversationId || 'public')}`);
      setMessages(res.data.messages || []);
    } catch (err) {
      toast({ title: 'Chatten kunne ikke hentes', description: err.response?.data?.detail || 'Prøv at åbne siden igen.', variant: 'destructive' });
    } finally {
      setMessagesLoading(false);
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      try {
        const [statusRes, participantsRes, conversationsRes] = await Promise.all([
          apiGet('/api/chat/status'),
          apiGet('/api/chat/participants'),
          apiGet('/api/chat/conversations'),
        ]);
        if (!mounted) return;
        setEnabled(statusRes.data.enabled !== false);
        setParticipants(participantsRes.data.participants || []);
        const nextConversations = conversationsRes.data.conversations || [];
        setConversations(nextConversations);
        const firstId = nextConversations[0]?.id || 'public';
        setActiveId(firstId);
        await loadMessages(firstId);
      } catch (err) {
        if (!mounted) return;
        setEnabled(false);
        setLoading(false);
        toast({ title: 'Chatten er ikke tilgængelig', description: err.response?.data?.detail || 'Kun deltagende gæster kan bruge chatten.', variant: 'destructive' });
      }
    };

    bootstrap();

    const socket = new WebSocket(chatSocketUrl());
    socketRef.current = socket;
    socket.onopen = () => mounted && setConnected(true);
    socket.onclose = () => mounted && setConnected(false);
    socket.onerror = () => mounted && setConnected(false);
    socket.onmessage = (event) => {
      if (!mounted) return;
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'message' && payload.message) {
          const conversationId = payload.conversation_id || payload.message.conversation_id || 'public';
          if (conversationId === activeIdRef.current) {
            setMessages((current) => {
              if (current.some((message) => message.id === payload.message.id)) return current;
              return [...current, payload.message];
            });
          } else {
            setConversations((current) => current.map((conversation) => (
              conversation.id === conversationId
                ? { ...conversation, unread_count: (conversation.unread_count || 0) + 1 }
                : conversation
            )));
          }
        }
        if (payload.type === 'status') setEnabled(payload.enabled !== false);
        if (payload.type === 'error') {
          toast({ title: 'Beskeden blev ikke sendt', description: payload.detail || 'Prøv igen.', variant: 'destructive' });
        }
      } catch {
        // Ignore malformed socket messages.
      }
    };

    return () => {
      mounted = false;
      socket.close();
    };
  }, [toast, loadMessages]);

  useEffect(() => {
    if (activeId) {
      setConversations((current) => current.map((conversation) => (
        conversation.id === activeId ? { ...conversation, unread_count: 0 } : conversation
      )));
      loadMessages(activeId);
    }
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (activeConversation?.type === 'group') {
      setGroupMemberDraft(activeConversation.member_ids || []);
      setOwnerTransferId('');
    } else {
      setGroupMemberDraft([]);
      setOwnerTransferId('');
    }
  }, [activeConversation?.id, activeConversation?.type, activeConversation?.member_ids]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, activeId]);

  const startDirectChat = async (guestId) => {
    try {
      const res = await apiPost('/api/chat/conversations/direct', { guest_id: guestId });
      await loadConversations();
      setActiveId(res.data.conversation.id);
    } catch (err) {
      toast({ title: 'Samtalen kunne ikke oprettes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const createGroupChat = async (event) => {
    event.preventDefault();
    if (!groupTitle.trim()) return;
    try {
      const res = await apiPost('/api/chat/conversations/group', { title: groupTitle, member_ids: selectedMembers });
      setGroupTitle('');
      setSelectedMembers([]);
      await loadConversations();
      setActiveId(res.data.conversation.id);
      toast({ title: 'Gruppechat oprettet' });
    } catch (err) {
      toast({ title: 'Gruppen kunne ikke oprettes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const renameGroup = async () => {
    if (!activeConversation || activeConversation.type !== 'group') return;
    const title = window.prompt('Nyt gruppenavn', activeConversation.title);
    if (!title) return;
    try {
      await apiPut(`/api/chat/conversations/${activeConversation.id}`, { title });
      await loadConversations();
      toast({ title: 'Gruppen er omdøbt' });
    } catch (err) {
      toast({ title: 'Gruppen kunne ikke omdøbes', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const toggleMember = (guestId) => {
    setSelectedMembers((current) => (
      current.includes(guestId) ? current.filter((id) => id !== guestId) : [...current, guestId]
    ));
  };

  const toggleGroupMember = (guestId) => {
    setGroupMemberDraft((current) => (
      current.includes(guestId) ? current.filter((id) => id !== guestId) : [...current, guestId]
    ));
  };

  const saveGroupMembers = async () => {
    if (!activeConversation || activeConversation.type !== 'group') return;
    try {
      await apiPut(`/api/chat/conversations/${activeConversation.id}`, { member_ids: groupMemberDraft });
      await loadConversations();
      toast({ title: 'Gruppemedlemmer opdateret' });
    } catch (err) {
      toast({ title: 'Medlemmer kunne ikke gemmes', description: err.response?.data?.detail || 'Kun gruppens opretter kan ændre medlemmer.', variant: 'destructive' });
    }
  };

  const transferGroupOwner = async () => {
    if (!activeConversation || activeConversation.type !== 'group' || !ownerTransferId) return;
    try {
      await apiPost(`/api/chat/conversations/${activeConversation.id}/transfer-owner`, { guest_id: ownerTransferId });
      await loadConversations();
      setOwnerTransferId('');
      toast({ title: 'Ejerskab er overdraget' });
    } catch (err) {
      toast({ title: 'Ejerskab kunne ikke overdrages', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const leaveGroup = async () => {
    if (!activeConversation || activeConversation.type !== 'group') return;
    try {
      await apiPost(`/api/chat/conversations/${activeConversation.id}/leave`, {});
      const next = await loadConversations();
      setActiveId(next[0]?.id || 'public');
      toast({ title: 'Du har forladt gruppen' });
    } catch (err) {
      toast({ title: 'Gruppen kunne ikke forlades', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    }
  };

  const insertEmoji = (emoji) => {
    setText((current) => `${current}${emoji}`.slice(0, 500));
    setShowEmojiPicker(false);
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const cleanText = text.trim();
    if (!cleanText || sending || !enabled || !activeConversation) return;
    setSending(true);
    setText('');
    setShowEmojiPicker(false);
    const payload = { text: cleanText, conversation_id: activeConversation.id };

    try {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify(payload));
      } else {
        const res = await apiPost('/api/chat/messages', payload);
        if (res.data.message) setMessages((current) => [...current, res.data.message]);
      }
    } catch (err) {
      setText(cleanText);
      toast({ title: 'Beskeden blev ikke sendt', description: err.response?.data?.detail || 'Prøv igen.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const sortedMessages = messages.filter((message) => !message.deleted);
  const directConversations = conversations.filter((conversation) => conversation.type === 'direct');
  const groupConversations = conversations.filter((conversation) => conversation.type === 'group');
  const publicConversation = conversations.find((conversation) => conversation.type === 'public') || { id: 'public', title: 'Fælleschat', type: 'public' };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-6xl flex-col animate-fade-in" data-testid="guest-chat-page">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Gæstechat</h1>
          <p className="mt-1 text-sm text-slate-400">Chat med deltagende gæster før og under eventet.</p>
        </div>
        <div className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${connected ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-slate-700 bg-slate-800/60 text-slate-400'}`}>
          {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {connected ? 'Live forbindelse' : 'Genopretter forbindelse'}
        </div>
      </div>

      {!enabled && (
        <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">
          Chatten er kun tilgængelig for gæster, der deltager.
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[20rem_1fr]">
        <aside className="space-y-4 rounded-xl border border-slate-700/50 bg-slate-900/60 p-3 lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto">
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Fælleschat</h2>
            <button
              type="button"
              onClick={() => setActiveId(publicConversation.id)}
              className={`w-full rounded-lg border px-3 py-3 text-left text-sm ${activeId === publicConversation.id ? 'border-blue-500/40 bg-blue-500/10 text-blue-100' : 'border-slate-800 bg-slate-950/40 text-slate-300 hover:bg-slate-800/70'}`}
            >
              <MessageCircle className="mr-2 inline h-4 w-4" />
              Fælleschat
            </button>
          </section>

          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Direkte beskeder</h2>
            <div className="space-y-2">
              {directConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setActiveId(conversation.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${activeId === conversation.id ? 'border-purple-500/40 bg-purple-500/10 text-purple-100' : 'border-slate-800 bg-slate-950/40 text-slate-300 hover:bg-slate-800/70'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate">{conversation.title}</span>
                    {!!conversation.unread_count && <span className="rounded-full bg-blue-500 px-2 py-0.5 text-xs text-white">{conversation.unread_count}</span>}
                  </span>
                </button>
              ))}
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/35 p-2">
                {participants.length === 0 ? (
                  <p className="px-1 py-2 text-xs text-slate-500">Ingen deltagende gæster at vælge.</p>
                ) : participants.map((guest) => (
                  <button key={guest.id} type="button" onClick={() => startDirectChat(guest.id)} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-xs text-slate-300 hover:bg-slate-800">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-[11px] text-blue-200">{initials(guest.name)}</span>
                    <span className="min-w-0">
                      <span className="block truncate">{guest.name}</span>
                      {(guest.relation || guest.title) && <span className="block truncate text-slate-500">{guest.relation || guest.title}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Gruppechats</h2>
            <div className="space-y-2">
              {groupConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setActiveId(conversation.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${activeId === conversation.id ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100' : 'border-slate-800 bg-slate-950/40 text-slate-300 hover:bg-slate-800/70'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate">{conversation.title}</span>
                    <span className="text-xs text-slate-500">{conversation.member_ids?.length || 0}</span>
                  </span>
                </button>
              ))}
            </div>

            <form onSubmit={createGroupChat} className="mt-3 space-y-2 rounded-lg border border-slate-800 bg-slate-950/35 p-3">
              <input className={inputClass} value={groupTitle} onChange={(event) => setGroupTitle(event.target.value)} placeholder="Gruppenavn" />
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {participants.map((guest) => (
                  <label key={guest.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
                    <input type="checkbox" checked={selectedMembers.includes(guest.id)} onChange={() => toggleMember(guest.id)} />
                    <span className="truncate">{guest.name}</span>
                  </label>
                ))}
              </div>
              <Button type="submit" disabled={!groupTitle.trim()} className="w-full bg-emerald-600 text-white hover:bg-emerald-500">
                <Plus className="h-4 w-4" />
                Opret gruppe
              </Button>
            </form>
          </section>
        </aside>

        <main className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-700/50 bg-slate-900/60">
          <div className="border-b border-slate-800 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-50">{activeConversation?.title || 'Fælleschat'}</h2>
                <p className="text-xs text-slate-500">
                  {activeConversation?.type === 'public' ? 'Alle deltagende gæster' : `${activeConversation?.members?.length || 0} medlemmer`}
                </p>
              </div>
              {activeConversation?.type === 'group' && (
                <div className="flex flex-col gap-2 sm:flex-row">
                  {isGroupOwner && (
                    <Button type="button" variant="outline" onClick={renameGroup} className="w-full border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 sm:w-auto">
                      Omdøb gruppe
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={leaveGroup} className="w-full border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20 sm:w-auto">
                    Forlad gruppe
                  </Button>
                </div>
              )}
            </div>
            {activeConversation?.type !== 'public' && activeConversation?.members?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {activeConversation.members.map((member) => (
                  <span key={member.id} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-1 text-xs text-slate-300">
                    <Users className="h-3 w-3" />
                    {member.name}
                  </span>
                ))}
              </div>
            )}
            {activeConversation?.type === 'group' && isGroupOwner && (
              <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/35 p-3">
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Medlemmer</p>
                  <Button type="button" size="sm" variant="outline" onClick={saveGroupMembers} className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700">
                    Gem medlemmer
                  </Button>
                </div>
                <div className="grid max-h-32 gap-1 overflow-y-auto sm:grid-cols-2">
                  {participants.map((guest) => (
                    <label key={guest.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
                      <input type="checkbox" checked={groupMemberDraft.includes(guest.id)} onChange={() => toggleGroupMember(guest.id)} />
                      <span className="truncate">{guest.name}</span>
                    </label>
                  ))}
                </div>
                {transferableMembers.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2 border-t border-slate-800 pt-3 sm:flex-row">
                    <select value={ownerTransferId} onChange={(event) => setOwnerTransferId(event.target.value)} className={inputClass}>
                      <option value="">Overdrag ejerskab...</option>
                      {transferableMembers.map((member) => (
                        <option key={member.id} value={member.id}>{member.name}</option>
                      ))}
                    </select>
                    <Button type="button" variant="outline" disabled={!ownerTransferId} onClick={transferGroupOwner} className="w-full border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 sm:w-auto">
                      Overdrag
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="min-h-[24rem] flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
            {loading || messagesLoading ? (
              <div className="flex h-full items-center justify-center py-12 text-slate-400">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Indlæser chat...
              </div>
            ) : sortedMessages.length === 0 ? (
              <div className="flex h-full min-h-[18rem] flex-col items-center justify-center text-center text-slate-500">
                <MessageCircle className="mb-3 h-8 w-8 text-slate-600" />
                <p className="text-sm">Ingen beskeder endnu.</p>
                <p className="mt-1 text-xs">Start samtalen, når chatten er åben.</p>
              </div>
            ) : (
              sortedMessages.map((message) => (
                <div key={message.id} className="flex gap-3 rounded-lg bg-slate-800/45 p-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-bold text-white">
                    {initials(message.guest_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <p className="text-sm font-medium text-slate-100">{message.guest_name || 'Gæst'}</p>
                      <span className="text-xs text-slate-500">{formatTime(message.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-300">{message.body || message.text}</p>
                  </div>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={sendMessage} className="border-t border-slate-800 p-3 sm:p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="relative flex-1">
                <textarea
                  value={text}
                  onChange={(event) => setText(event.target.value.slice(0, 500))}
                  placeholder={enabled ? 'Skriv en besked...' : 'Chatten er lukket'}
                  disabled={!enabled || !activeConversation}
                  rows={2}
                  className="min-h-[3rem] w-full resize-none rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-2 pr-12 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
                  data-testid="guest-chat-input"
                />
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker((current) => !current)}
                  disabled={!enabled || !activeConversation}
                  className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-slate-100 disabled:opacity-40"
                  aria-label="Vælg emoji"
                >
                  <Smile className="h-4 w-4" />
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-12 right-0 z-20 grid w-64 grid-cols-6 gap-1 rounded-lg border border-slate-700 bg-slate-950 p-2 shadow-xl">
                    {emojis.map((emoji) => (
                      <button key={emoji} type="button" onClick={() => insertEmoji(emoji)} className="flex h-9 w-9 items-center justify-center rounded text-lg hover:bg-slate-800">
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" disabled={!enabled || !text.trim() || sending || !activeConversation} className="h-12 w-full bg-blue-600 text-white hover:bg-blue-500 sm:w-auto sm:self-end" data-testid="guest-chat-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send
              </Button>
            </div>
            <p className="mt-2 text-right text-xs text-slate-600">{text.length}/500</p>
          </form>
        </main>
      </div>
    </div>
  );
}
